import { fetchOfferListFilters } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getOfferListKey from 'lib/offer/offerListKey'
import { useEffect, useMemo } from 'react'

/***
 *  We don't sum up the value together, instead reset the value to zero
 *  because we can not guarantee that sum up all filters count is correct due to potentially deduplication
 */
function mergeValueCountArrayToSet(set: Set<string>, valueCounts: Array<{ value: string, count: number }>) {
  for (const valueCount of valueCounts) {
    const { value } = valueCount
    set.add(value)
  }
}

function mergeRecordValueToSet(set: Set<string>, record: Record<string, number>) {
  for (const [key, _] of Object.entries(record)) {
    set.add(key)
  }
}

function mergeNestedRecordToSet(set: Set<string>, nestedRecord: Record<string, Record<string, number>>) {
  for (const [key, innerRecord] of Object.entries(nestedRecord)) {
    for (const [innerKey, _] of Object.entries(innerRecord)) {
      const hashedKey = key + '#' + innerKey
      set.add(hashedKey)
    }
  }
}

function populateRecordValueFromSet(set: Set<string>, record: Record<string, number>, toPopulateRecord: Record<string, number>) {
  for (const key of Object.keys(record)) {
    if (set.has(key)) {
      toPopulateRecord[key] = 0
    }
  }
}

function populateNestedRecordValueFromSet(set: Set<string>, toPopulateRecord: Record<string, Record<string, number>>) {
  for (const key of set.values()) {
    if (key.includes('#')) {
      const [key1, key2] = key.split('#')
      if (!toPopulateRecord[key1]) {
        toPopulateRecord[key1] = {}
      }
      toPopulateRecord[key1][key2] = 0
    }
  }
}

export function useMultipleOfferListFilters(filtersList: Array<App.OfferListFilters>): App.OfferListFilterOptions {
  const dispatch = useAppDispatch()
  useEffect(() => {
    for (const filters of filtersList) {
      dispatch(fetchOfferListFilters(filters))
    }
  }, [filtersList, dispatch])
  const offerListFilterOption = useAppSelector(state => state.offer.offerListFilterOptions)
  const listFilters = useMemo(() => {
    const combinedFilters: App.OfferListFilterOptions = {
      error: undefined,
      fetching: false,
      filterOrder: {
        amenities: {},
        holidayTypes: {},
      },
      filters: {
        amenities: {},
        bedrooms: {},
        campaigns: {},
        holidayTypes: {},
        offerTypes: {},
        propertyTypes: {},
        luxPlusFeatures: {},
        loyalty: {},
        agentHubFeatures: {},
        paymentOptions: {},
        chains: {},
        total: 0,
      },
      orderedFilters: {
        amenities: [],
        holidayTypes: [],
      },
    }

    const filtersCategorySet = new Set<string>()
    const orderedFiltersCategorySet = new Set<string>()
    const orderedFiltersHolidayTypesCategorySet = new Set<string>()
    const filtersOrderAmenitiesCategorySet = new Set<string>()
    const filtersOrderHolidayTypesOrderSet = new Set<string>()

    filtersList.forEach(filter => {
      const key = getOfferListKey(filter)
      const filterOptions = offerListFilterOption[key]
      if (!filterOptions) return

      if (filterOptions?.filters) {
        mergeRecordValueToSet(filtersCategorySet, filterOptions.filters.holidayTypes)
        mergeRecordValueToSet(filtersCategorySet, filterOptions.filters.propertyTypes)
        mergeRecordValueToSet(filtersCategorySet, filterOptions.filters.campaigns)
        mergeRecordValueToSet(filtersCategorySet, filterOptions.filters.amenities)
        mergeRecordValueToSet(filtersCategorySet, filterOptions.filters.bedrooms)
        if (filterOptions.filters.customerRatings) {
          mergeRecordValueToSet(filtersCategorySet, filterOptions.filters.customerRatings)
        }
        mergeRecordValueToSet(filtersCategorySet, filterOptions.filters.luxPlusFeatures)
        mergeRecordValueToSet(filtersCategorySet, filterOptions.filters.loyalty)
        mergeRecordValueToSet(filtersCategorySet, filterOptions.filters.agentHubFeatures)
        mergeRecordValueToSet(filtersCategorySet, filterOptions.filters.offerTypes)
        filtersCategorySet.add('total')
        if (filterOptions.filters.inclusions) {
          mergeNestedRecordToSet(filtersCategorySet, filterOptions.filters.inclusions)
        }
        // combinedFilters.filters for simplicity combined it here otherwise we need different map for different fields
        if (combinedFilters.filters) {
          populateRecordValueFromSet(filtersCategorySet, filterOptions.filters.holidayTypes, combinedFilters.filters.holidayTypes)
          populateRecordValueFromSet(filtersCategorySet, filterOptions.filters.propertyTypes, combinedFilters.filters.propertyTypes)
          populateRecordValueFromSet(filtersCategorySet, filterOptions.filters.campaigns, combinedFilters.filters.campaigns)
          populateRecordValueFromSet(filtersCategorySet, filterOptions.filters.amenities, combinedFilters.filters.amenities)
          populateRecordValueFromSet(filtersCategorySet, filterOptions.filters.bedrooms, combinedFilters.filters.bedrooms)
          if (filterOptions.filters.customerRatings && combinedFilters.filters.customerRatings) {
            populateRecordValueFromSet(filtersCategorySet, filterOptions.filters.customerRatings, combinedFilters.filters.customerRatings)
          }
          populateRecordValueFromSet(filtersCategorySet, filterOptions.filters.luxPlusFeatures, combinedFilters.filters.luxPlusFeatures)
          populateRecordValueFromSet(filtersCategorySet, filterOptions.filters.loyalty, combinedFilters.filters.loyalty)
          populateRecordValueFromSet(filtersCategorySet, filterOptions.filters.agentHubFeatures, combinedFilters.filters.agentHubFeatures)
          populateRecordValueFromSet(filtersCategorySet, filterOptions.filters.offerTypes, combinedFilters.filters.offerTypes)
          if (filterOptions.filters.inclusions) {
            if (!combinedFilters.filters.inclusions) {
              combinedFilters.filters.inclusions = {}
            }
            populateNestedRecordValueFromSet(filtersCategorySet, combinedFilters.filters.inclusions)
          }
        }
      }

      if (filterOptions.orderedFilters && combinedFilters.orderedFilters) {
        // Merge amenities
        if (filterOptions.orderedFilters.amenities) {
          mergeValueCountArrayToSet(orderedFiltersCategorySet, filterOptions.orderedFilters.amenities)
        }

        // Merge holidayTypes
        if (filterOptions.orderedFilters.holidayTypes) {
          mergeValueCountArrayToSet(orderedFiltersHolidayTypesCategorySet, filterOptions.orderedFilters.holidayTypes)
        }
      }

      // Merge filter order
      if (filterOptions.filterOrder && combinedFilters.filterOrder) {
        // Merge amenities
        if (filterOptions.filterOrder?.amenities) {
          mergeRecordValueToSet(filtersOrderAmenitiesCategorySet, filterOptions.filterOrder.amenities)
        }

        // Merge holidayTypes
        if (filterOptions.filterOrder?.holidayTypes) {
          mergeRecordValueToSet(filtersOrderHolidayTypesOrderSet, filterOptions.filterOrder.holidayTypes)
        }
      }
    })

    // After all filters are processed, update the combined filters
    if (orderedFiltersCategorySet.size > 0 && combinedFilters.orderedFilters) {
      combinedFilters.orderedFilters.amenities = Array.from(orderedFiltersCategorySet.values().map((category) => ({ value: category, count: 0 })))
    }

    if (orderedFiltersHolidayTypesCategorySet.size > 0 && combinedFilters.orderedFilters) {
      combinedFilters.orderedFilters.holidayTypes = Array.from((orderedFiltersHolidayTypesCategorySet.values()).map((category) => ({ value: category, count: 0 })))
    }

    if (filtersOrderAmenitiesCategorySet.size > 0 && combinedFilters.filterOrder) {
      combinedFilters.filterOrder.amenities = Object.fromEntries(filtersOrderAmenitiesCategorySet.values().map(category => [category, 0]))
    }
    if (filtersOrderHolidayTypesOrderSet.size > 0 && combinedFilters.filterOrder) {
      combinedFilters.filterOrder.holidayTypes = Object.fromEntries(filtersOrderHolidayTypesOrderSet.values().map(category => [category, 0]))
    }

    if (combinedFilters.filters && filtersCategorySet.has('total')) {
      combinedFilters.filters.total = 0
    }
    return combinedFilters
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersList])

  return listFilters
}
